import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { invalidateAuthCache } from '../services/apiService';

interface AuthContextValue {
  accessToken: string | null;
  login: (token: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [accessToken, setAccessToken] = useState<string | null>(
    () => localStorage.getItem('auth0_access_token')
  );

  // Handle OAuth callback: NestJS redirects here with ?token= after Google login
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');
    if (urlToken) {
      window.history.replaceState({}, '', window.location.pathname);
      localStorage.setItem('auth0_access_token', urlToken);
      invalidateAuthCache();
      setAccessToken(urlToken);
    }
  }, []);

  const login = useCallback((token: string) => {
    localStorage.setItem('auth0_access_token', token);
    invalidateAuthCache();
    setAccessToken(token);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('auth0_access_token');
    invalidateAuthCache();
    setAccessToken(null);
  }, []);

  return (
    <AuthContext.Provider value={{ accessToken, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
